/**
 * Inspects media files in the browser (WebCodecs via mediabunny for audio/video, the browser's
 * image decoder for stills). Replaces server-side ffprobe.
 */
import { InputDisposedError } from 'mediabunny';
import { decodeImage, type MediaInput } from '@ve/engine';
import { UnsupportedMediaError, type MediaAnalyzer, type MediaProbe } from '../types.ts';
import { blobReader, exifCaptureDate, mp4CreationDate } from './capture-date.ts';
import { openInput, toBlob } from './input.ts';
import {
  extensionOf,
  undecodableAudioMessage,
  undecodableVideoMessage,
  unknownFormatMessage,
  unsupportedImageMessage,
} from './messages.ts';

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'bmp', 'ico', 'heic', 'heif']);

/** Photos larger than this are only scanned at the start for EXIF (where JPEG keeps it). */
const EXIF_FULL_SCAN_MAX_BYTES = 64 * 1024 * 1024;
const EXIF_HEAD_BYTES = 1024 * 1024;

/** Video tracks with at most this many frames are cover art (e.g. album art in an MP3/M4A). */
const COVER_ART_MAX_PACKETS = 1;

function isImage(input: MediaInput, fileName: string): boolean {
  const ext = extensionOf(fileName);
  if (IMAGE_EXTENSIONS.has(ext) || unsupportedImageMessage(ext)) return true;
  return typeof input !== 'string' && input.type.startsWith('image/');
}

export class BrowserMediaAnalyzer implements MediaAnalyzer {
  async probe(input: MediaInput, fileName: string): Promise<MediaProbe> {
    if (isImage(input, fileName)) return this.probeImage(input, fileName);
    return this.probeAudioVideo(input, fileName);
  }

  private async probeImage(input: MediaInput, fileName: string): Promise<MediaProbe> {
    const unsupported = unsupportedImageMessage(extensionOf(fileName));
    if (unsupported) throw new UnsupportedMediaError(unsupported);
    let bitmap: ImageBitmap;
    let blob: Blob;
    try {
      blob = await toBlob(input);
      // Default orientation is "from-image": EXIF rotation is applied, so dimensions are as displayed.
      bitmap = await decodeImage(blob);
    } catch (err) {
      const reason = err instanceof Error ? ` (${err.message})` : '';
      throw new UnsupportedMediaError(`“${fileName}” could not be decoded as an image.${reason}`);
    }
    const probe: MediaProbe = {
      kind: 'image',
      width: bitmap.width,
      height: bitmap.height,
      hasAudio: false,
      hasVideo: true,
    };
    bitmap.close();
    const capturedAt = await imageCaptureDate(blob);
    if (capturedAt) probe.capturedAt = capturedAt.toISOString();
    return probe;
  }

  private async probeAudioVideo(source: MediaInput, fileName: string): Promise<MediaProbe> {
    const input = openInput(source);
    try {
      if (!(await input.canRead())) throw new UnsupportedMediaError(unknownFormatMessage(fileName));

      let video = await input.getPrimaryVideoTrack();
      if (
        video &&
        (await video.computePacketStats(COVER_ART_MAX_PACKETS + 1)).packetCount <= COVER_ART_MAX_PACKETS
      ) {
        video = null; // a still cover image, not motion video
      }
      const audio = await input.getPrimaryAudioTrack();
      if (!video && !audio) throw new UnsupportedMediaError(`“${fileName}” contains no video or audio.`);

      const warnings: string[] = [];
      if (video && !(await video.canDecode())) {
        throw new UnsupportedMediaError(undecodableVideoMessage(await video.getCodec()));
      }
      let hasAudio = !!audio;
      if (audio && !(await audio.canDecode())) {
        const message = undecodableAudioMessage(await audio.getCodec());
        if (!video) throw new UnsupportedMediaError(message);
        // Usable as a silent video; tell the user why there's no sound.
        hasAudio = false;
        warnings.push(`${message} The video was imported without sound.`);
      }

      const duration = await this.duration(input);
      const probe: MediaProbe = { kind: video ? 'video' : 'audio', duration, hasAudio, hasVideo: !!video };
      if (video) {
        probe.width = await video.getDisplayWidth();
        probe.height = await video.getDisplayHeight();
        const stats = await video.computePacketStats(120);
        if (stats.averagePacketRate > 0 && Number.isFinite(stats.averagePacketRate)) {
          probe.fps = Math.round(stats.averagePacketRate * 1000) / 1000;
        }
      }
      const capturedAt = await this.captureDate(input, source);
      if (capturedAt) probe.capturedAt = capturedAt.toISOString();
      if (warnings.length > 0) probe.warnings = warnings;
      return probe;
    } catch (err) {
      if (err instanceof UnsupportedMediaError || err instanceof InputDisposedError) throw err;
      throw new UnsupportedMediaError(
        `${unknownFormatMessage(fileName)} (${err instanceof Error ? err.message : String(err)})`,
      );
    } finally {
      input.dispose();
    }
  }

  /**
   * Recording date: an explicit metadata tag (e.g. Apple's creation date, which carries the time
   * zone) wins over the MP4/MOV movie header that most cameras set. Best effort: never fails a probe.
   */
  private async captureDate(
    input: ReturnType<typeof openInput>,
    source: MediaInput,
  ): Promise<Date | undefined> {
    try {
      const tags = await input.getMetadataTags();
      if (tags.date && Number.isFinite(tags.date.getTime())) return tags.date;
      if (typeof source !== 'string') return await mp4CreationDate(blobReader(source), source.size);
    } catch {
      /* metadata is optional */
    }
    return undefined;
  }

  private async duration(input: ReturnType<typeof openInput>): Promise<number | undefined> {
    const fromMetadata = await input.getDurationFromMetadata().catch(() => null);
    const duration = fromMetadata && fromMetadata > 0 ? fromMetadata : await input.computeDuration();
    return duration > 0 ? duration : undefined;
  }
}

async function imageCaptureDate(blob: Blob): Promise<Date | undefined> {
  try {
    const bytes = blob.size <= EXIF_FULL_SCAN_MAX_BYTES ? blob : blob.slice(0, EXIF_HEAD_BYTES);
    return exifCaptureDate(new Uint8Array(await bytes.arrayBuffer()));
  } catch {
    return undefined; // metadata is optional
  }
}
